import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { LoadingState } from "@/components/ui/loading-state";
import { PlanSettingsFields } from "@/components/PlanSettingsFields";
import { OrganizationPlan, PlanSettings } from "@/models/package";
import { usePackages } from "@/pages/packages/hooks/usePackages";
import { useOrganizationPlan, useUpdateOrganizationPlan } from "../hooks/useOrganizationPlan";

const CUSTOM = "__custom__";

interface Draft extends PlanSettings {
  package: string | null;
}

const draftOf = (plan: OrganizationPlan): Draft => ({
  package: plan.package,
  enabledServices: plan.enabledServices,
  limits: plan.limits
});

/** The organization's optional services and limits, starting from a package. */
export function PlanCard({ organizationId }: { organizationId: string }) {
  const { data: plan, isLoading, error, refetch } = useOrganizationPlan(organizationId);
  const { data: packages = [] } = usePackages();
  const { mutate: savePlan, isPending } = useUpdateOrganizationPlan(organizationId);
  const [draft, setDraft] = useState<Draft | null>(null);

  useEffect(() => {
    if (plan) setDraft(draftOf(plan));
  }, [plan]);

  const isDirty = useMemo(
    () => !!plan && !!draft && JSON.stringify(draft) !== JSON.stringify(draftOf(plan)),
    [plan, draft]
  );

  if (isLoading || error || !plan || !draft) {
    return (
      <LoadingState
        isLoading={isLoading}
        error={error}
        loadingText="Loading plan..."
        errorTitle="Failed to load plan"
        onRetry={() => refetch()}
      />
    );
  }

  // Picking a package fills the form from it; the values stay editable after.
  const applyPackage = (key: string) => {
    const pkg = packages.find((p) => p.key === key);
    setDraft(
      pkg
        ? { package: pkg.key, enabledServices: pkg.enabledServices, limits: pkg.limits }
        : { ...draft, package: null }
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Plan
          {plan.legacy && <Badge variant="secondary">From before packages</Badge>}
        </CardTitle>
        <CardDescription>
          {plan.legacy
            ? "This organization keeps Campaigns, Catalogue and Data Hub with no limits until a package is applied."
            : "Switched-off features are hidden for every member and refused by the API."}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="max-w-xs space-y-1">
          <Label>Package</Label>
          <Select value={draft.package ?? CUSTOM} onValueChange={applyPackage}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {packages.map((pkg) => (
                <SelectItem key={pkg.key} value={pkg.key}>
                  {pkg.name}
                </SelectItem>
              ))}
              <SelectItem value={CUSTOM}>Custom</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <PlanSettingsFields
          idPrefix="organization-plan"
          value={draft}
          onChange={(settings) => setDraft({ ...draft, ...settings })}
          disabled={isPending}
        />
        <div className="flex justify-end">
          <Button onClick={() => savePlan(draft)} disabled={!isDirty || isPending}>
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save plan
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
